import React, { FC } from 'react';

import { DataAccessPrincipal } from '@ariksa/inventory-core';
import { Box, HStack, Spinner, Stack } from '@chakra-ui/react';
import { useSelector } from 'react-redux';

import { WithResourceIcon } from 'components/DataDisplay';
import { CustomTooltip } from 'components/DataDisplay/Tooltip/CustomTooltip';
import { useOpenLinkedResource } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Identity/Principals/useOpenLinkedResource';
import { selectActiveResource } from 'containers/ActiveCloudResource/selectors';

interface RoleListProps {
  roles: DataAccessPrincipal[];
  // The identity grants it itself.
  direct: boolean;
  // Roles the API counted but didn't list.
  more?: number;
}

/**
 * Who grants access: "Direct", then each role, which opens in the drawer when
 * clicked, as on the Principals tab.
 */
export const RoleList: FC<RoleListProps> = props => {
  const { roles, direct, more = 0 } = props;
  // A role is in the same Snowflake account as the user it is granted to.
  const { resourceAccountId } = useSelector(selectActiveResource);
  const { openResource, isOpening } = useOpenLinkedResource();
  return (
    <Stack spacing={1}>
      {direct && <Box fontWeight={500}>Direct</Box>}
      {roles.map(role => (
        <CustomTooltip key={role.uuid} label="Open this role">
          <HStack
            as="button"
            type="button"
            spacing={1}
            maxW="full"
            color="primary"
            _hover={{ textDecoration: 'underline' }}
            onClick={() =>
              openResource({
                uuid: role.uuid,
                name: role.name,
                account: resourceAccountId,
                resourceType: role.type,
              })
            }
          >
            <WithResourceIcon resourceType={role.type} iconSize="xs">
              <Box textAlign="left">{role.name ?? role.uuid}</Box>
            </WithResourceIcon>
            {isOpening(role.uuid) && <Spinner size="xs" color="primary" />}
          </HStack>
        </CustomTooltip>
      ))}
      {more > 0 && <Box color="gray.400">and {more.toLocaleString()} more</Box>}
      {!direct && !roles.length && <Box color="gray.400">-</Box>}
    </Stack>
  );
};
